/**
 * Utilities for managing recently used chains, tokens, and contract addresses in localStorage
 */

const RECENT_CHAINS_KEY = "b3os_recent_chains";
const RECENT_CONTRACTS_KEY = "b3os_recent_contracts";
const RECENT_TOKENS_KEY = "b3os_recent_tokens";
const RECENT_MORPHO_VAULTS_KEY = "b3os_recent_morpho_vaults";
const MAX_RECENT_ITEMS = 5;

export interface RecentChain {
  chainId: number;
  name: string;
  iconUrl?: string;
  lastUsed: number;
}

export interface RecentContract {
  address: string;
  chainId: number;
  name?: string;
  lastUsed: number;
}

export interface RecentToken {
  address: string;
  chainId: number;
  symbol: string;
  name: string;
  logoURI?: string | null;
  lastUsed: number;
}

// Recent Chains
export function getRecentChains(): RecentChain[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(RECENT_CHAINS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function addRecentChain(chain: Omit<RecentChain, "lastUsed">): void {
  if (typeof window === "undefined") return;
  try {
    const recent = getRecentChains();
    const filtered = recent.filter(c => c.chainId !== chain.chainId);
    const updated = [{ ...chain, lastUsed: Date.now() }, ...filtered].slice(0, MAX_RECENT_ITEMS);
    localStorage.setItem(RECENT_CHAINS_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Failed to save recent chain:", error);
  }
}

// Recent Contracts
export function getRecentContracts(): RecentContract[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(RECENT_CONTRACTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function addRecentContract(contract: Omit<RecentContract, "lastUsed">): void {
  if (typeof window === "undefined") return;
  try {
    const recent = getRecentContracts();
    const key = `${contract.chainId}-${contract.address.toLowerCase()}`;
    const filtered = recent.filter(c => `${c.chainId}-${c.address.toLowerCase()}` !== key);
    const updated = [{ ...contract, lastUsed: Date.now() }, ...filtered].slice(0, MAX_RECENT_ITEMS);
    localStorage.setItem(RECENT_CONTRACTS_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Failed to save recent contract:", error);
  }
}

// Recent Tokens
export function getRecentTokens(): RecentToken[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(RECENT_TOKENS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function addRecentToken(token: Omit<RecentToken, "lastUsed">): void {
  if (typeof window === "undefined") return;
  try {
    const recent = getRecentTokens();
    // Key by chainId + address to allow same token on different chains
    const key = `${token.chainId}-${token.address.toLowerCase()}`;
    const filtered = recent.filter(t => `${t.chainId}-${t.address.toLowerCase()}` !== key);
    const updated = [{ ...token, lastUsed: Date.now() }, ...filtered].slice(0, MAX_RECENT_ITEMS);
    localStorage.setItem(RECENT_TOKENS_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Failed to save recent token:", error);
  }
}

// Recent Morpho Vaults
export interface RecentMorphoVault {
  address: string;
  chainId: number;
  name: string;
  assetSymbol: string;
  netApy: number;
  lastUsed: number;
}

export function getRecentMorphoVaults(): RecentMorphoVault[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(RECENT_MORPHO_VAULTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function addRecentMorphoVault(vault: Omit<RecentMorphoVault, "lastUsed">): void {
  if (typeof window === "undefined") return;
  try {
    const recent = getRecentMorphoVaults();
    const key = `${vault.chainId}-${vault.address.toLowerCase()}`;
    const filtered = recent.filter(v => `${v.chainId}-${v.address.toLowerCase()}` !== key);
    const updated = [{ ...vault, lastUsed: Date.now() }, ...filtered].slice(0, MAX_RECENT_ITEMS);
    localStorage.setItem(RECENT_MORPHO_VAULTS_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Failed to save recent Morpho vault:", error);
  }
}

// Recent Morpho Markets
const RECENT_MORPHO_MARKETS_KEY = "b3os_recent_morpho_markets";

export interface RecentMorphoMarket {
  marketId: string;
  chainId: number;
  collateralSymbol: string;
  loanSymbol: string;
  loanAddress: string;
  lltvPercent: number;
  borrowApy: number;
  lastUsed: number;
}

export function getRecentMorphoMarkets(): RecentMorphoMarket[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(RECENT_MORPHO_MARKETS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function addRecentMorphoMarket(market: Omit<RecentMorphoMarket, "lastUsed">): void {
  if (typeof window === "undefined") return;
  try {
    const recent = getRecentMorphoMarkets();
    const key = `${market.chainId}-${market.marketId.toLowerCase()}`;
    const filtered = recent.filter(m => `${m.chainId}-${m.marketId.toLowerCase()}` !== key);
    const updated = [{ ...market, lastUsed: Date.now() }, ...filtered].slice(0, MAX_RECENT_ITEMS);
    localStorage.setItem(RECENT_MORPHO_MARKETS_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Failed to save recent Morpho market:", error);
  }
}
